import React from 'react';
import { Link } from 'react-router-dom';
import './ProductSection.css';

const carpelDarkLogo = '/images/carpel-dark-trimmed.png';
const carpelLightLogo = '/images/carpel-light-trimmed.png';

function ProductSection({ id = 'products-sec' }) {
  return (
    <section className="products-sec" id={id}>
      <div className="container">
        {/* Section Heading */}
        <div className="text-center mb-5" data-aos="zoom-in" data-aos-easing="linear" data-aos-duration="800">
          <h2 className="head">
            <span>Our Products</span>
            Proprietary Enterprise Software by Stamens
          </h2>
          <p className="text-muted mt-2" style={{ maxWidth: '680px', margin: '0 auto' }}>
            Production-ready digital platforms engineered in-house to solve mission-critical operational and document scalability challenges.
          </p>
        </div>

        {/* Product 1: Carpel Full-Width Split Card */}
        <div className="product-banner-card" data-aos="fade-up" data-aos-easing="linear" data-aos-duration="800">
          <div className="row align-items-center gy-4">
            {/* Left: Branding & Pitch */}
            <div className="col-lg-5">
              <div className="product-banner-header">
                <div className="product-logo-container" title="Carpel Platform">
                  <img src={carpelDarkLogo} alt="Carpel" className="logo-dark" />
                  <img src={carpelLightLogo} alt="Carpel" className="logo-light" />
                </div>
                <span className="product-banner-badge">Core Capabilities</span>
              </div>

              <div className="product-banner-tagline">Automation agents that execute real workflows</div>
              <p className="product-banner-desc">
                Intelligent automation agents that integrate with your existing tools to orchestrate real business workflows—triggering actions, synchronizing data, and completing multi-step processes automatically. Built for rapid deployment with minimal setup.
              </p>

              <div className="product-banner-ctas">
                <a
                  href="https://carpel.io"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-banner-primary"
                >
                  Visit Carpel <i className="bi bi-box-arrow-up-right"></i>
                </a>
                <a
                  href="https://carpel.io/book-a-demo"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-banner-secondary"
                >
                  Book a Demo <i className="bi bi-box-arrow-up-right"></i>
                </a>
              </div>
            </div>

            {/* Right: 2x2 Feature Matrix */}
            <div className="col-lg-7">
              <div className="product-banner-grid">
                <div className="product-feature-tile">
                  <i className="bi bi-soundwave"></i>
                  <div className="product-feature-title">QA Regression &amp; E2E Suites</div>
                  <div className="product-feature-desc">Execute structured, multi-step regression suites across complex web applications with automated pass/fail evaluations and visual evidence.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-arrow-repeat"></i>
                  <div className="product-feature-title">Conversational Exploratory Testing</div>
                  <div className="product-feature-desc">Perform ad-hoc web tasks and exploratory UI checks in a modern chat interface without writing code or pre-configuring test cases.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-bezier2"></i>
                  <div className="product-feature-title">Multi-Role RBAC Automation</div>
                  <div className="product-feature-desc">Automate complex enterprise workflows that require logging in as multiple different user roles securely and seamlessly.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-box"></i>
                  <div className="product-feature-title">Executive AI Reporting</div>
                  <div className="product-feature-desc">Automatically generate AI-synthesized reasoning summaries and comprehensive audit reports to share directly with your stakeholders.</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Product 2: DoResize Full-Width Split Card */}
        <div className="product-banner-card" data-aos="fade-up" data-aos-easing="linear" data-aos-duration="800">
          <div className="row align-items-center gy-4">
            {/* Left: Branding & Pitch */}
            <div className="col-lg-5">
              <div className="product-banner-header">
                <div className="resizer-icon-box" title="DoResize">
                  <i className="bi bi-aspect-ratio-fill"></i>
                </div>
                <span className="product-banner-badge">Document &amp; Photo Utility</span>
              </div>

              <h3 className="product-banner-title">DoResize</h3>
              <div className="product-banner-tagline">Smart Document, Photo &amp; Passport Resizing Suite</div>
              <p className="product-banner-desc">
                An intuitive, all-in-one browser utility that converts any document, photo, or passport image to custom dimensions. Auto-arrange multiple images onto standard sheet sizes (like A4) and instantly print or download high-resolution PDFs.
              </p>

              <div className="product-banner-ctas">
                <a
                  href="https://www.doresize.com/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-banner-primary"
                >
                  Visit doresize.com <i className="bi bi-box-arrow-up-right"></i>
                </a>
                <Link to="/contact" className="btn-banner-secondary">
                  Inquire Now
                </Link>
              </div>
            </div>

            {/* Right: 2x2 Feature Matrix */}
            <div className="col-lg-7">
              <div className="product-banner-grid">
                <div className="product-feature-tile">
                  <i className="bi bi-person-bounding-box"></i>
                  <div className="product-feature-title">Passport &amp; ID Presets</div>
                  <div className="product-feature-desc">Instantly resize photos to exact international passport, visa, and government ID standards in seconds.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-grid-3x3-gap-fill"></i>
                  <div className="product-feature-title">Smart Auto-Arrange</div>
                  <div className="product-feature-desc">Automatically tile and arrange multiple photos on sheet sizes like A4 to maximize space and eliminate waste.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-printer-fill"></i>
                  <div className="product-feature-title">Print &amp; PDF Download</div>
                  <div className="product-feature-desc">One-click generation of print-ready, high-resolution PDFs or downloadable image files ready for printing.</div>
                </div>

                <div className="product-feature-tile">
                  <i className="bi bi-images"></i>
                  <div className="product-feature-title">Multi-Format Batch Support</div>
                  <div className="product-feature-desc">Upload, resize, crop, and arrange multiple document and photo formats simultaneously in your browser.</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default ProductSection;
